import React from 'react';
import { Flex, Table, Typography } from 'antd';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import { mdiCalendarBlankOutline } from '@mdi/js';
import Ninja from '@ninja';
import Icon from '@mdi/react';
import {
  RoiFormatter,
  RoasFormatter,
  SpentFormatter,
  DotDigitFormatter,
} from 'components/tables/NinjaTable/formatters';
import getFormattedValue from 'store/redux/utils/getFormattedValue';

const baseColumns = [
  {
    key: 'date',
    title: <span className='text-xs font-medium'>Date</span>,
    dataIndex: 'date',
    width: 120,
    render: (_, row) => {
      const date = new Date(row.date);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return (
        <Flex gap={8} align='center'>
          <Icon path={mdiCalendarBlankOutline} size={0.6} color={Ninja.colors.light} />
          <span className='text-sm font-normal text-color-dark'>{formattedDate}</span>
        </Flex>
      );
    },
  },
  {
    key: 'ad_spent',
    width: 100,
    title: <span className='text-xs font-medium'>Ad Spent</span>,
    dataIndex: 'spent',
    render: (_, row) => (
      <Typography className='text-sm font-normal'>{getFormattedValue(row.ad_spent)}</Typography>
    ),
  },

  {
    key: 'gross_income',
    title: <span className='text-xs font-medium'>Gross Income</span>,
    width: 100,
    sortable: true,
    render: (_, row) => (
      <Typography className='text-sm font-normal'>{getFormattedValue(row.gross_income)}</Typography>
    ),
  },
  {
    key: 'gross_profit',
    title: <span className='text-xs font-medium'>Gross Profit</span>,
    width: 100,
    sortable: true,
    render: (_, row) => (
      <Typography className='text-sm font-normal'>{getFormattedValue(row.gross_profit)}</Typography>
    ),
  },
  {
    key: 'net_profit',
    width: 100,
    title: <span className='text-xs font-medium'>Net Profit</span>,
    render: (_, row) => (
      <Typography className='text-sm font-normal'>
        <DotDigitFormatter
          column={{ key: 'net_profit', type: 'profit', currency: true }}
          row={row}
        />
      </Typography>
    ),
  },
  {
    key: 'roi',
    title: <span className='text-xs font-medium'>ROI</span>,
    width: 70,
    dataIndex: 'roi',
    render: (_, row) => <RoiFormatter row={row} className='font-normal' />,
  },
  {
    key: 'roas',
    title: <span className='text-xs font-medium'>ROAS</span>,
    width: 100,
    render: (_, row) => <RoasFormatter row={row} className='font-normal' />,
  },
  {
    key: 'deals_count',
    title: <span className='text-xs font-medium'>Deals</span>,
    render: (_, row) => (
      <Typography className='text-sm font-normal'>{getFormattedNumber(row.deals_count)}</Typography>
    ),
  },
  {
    key: 'leads_count',
    title: <span className='text-xs font-medium'>leads</span>,
    render: (_, row) => (
      <Typography className='text-sm font-normal'>{getFormattedNumber(row.leads_count)}</Typography>
    ),
  },
];

const DailyStatisticsCustomData = ({ totals, data, loading }) => {
  if (!data || data.length === 0) return null;

  const renderTotalCell = (col) => {
    switch (col.key) {
      case 'date':
        return 'Totals';
      case 'ad_spent':
        return <SpentFormatter row={totals} fontWeight='medium' />;

      case 'gross_income':
        return getFormattedValue(totals.gross_income);

      case 'gross_profit':
        return getFormattedValue(totals.gross_profit);
      case 'net_profit':
        return (
          <strong className='text-sm'>
            <DotDigitFormatter
              column={{ key: 'net_profit', type: 'profit', currency: true }}
              row={totals}
            />
          </strong>
        );
      case 'roi':
        return <RoiFormatter value={totals.roi} />;

      case 'roas':
        return <RoasFormatter value={totals.roas} />;
      case 'deals_count':
        return getFormattedNumber(totals.deals_count);

      case 'leads_count':
        return getFormattedNumber(totals.leads_count);
    }
  };

  return (
    <Table
      rowKey='date'
      columns={baseColumns}
      dataSource={data}
      size='small'
      loading={loading}
      pagination={false}
      scroll={{ x: 'max-content', y: 285 }}
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row className='bg-background-light'>
            {baseColumns.map((col) => (
              <Table.Summary.Cell key={col.key} index={col.key} className='font-bold text-sm'>
                {renderTotalCell(col)}
              </Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
};

export default DailyStatisticsCustomData;
